/**
 * Column layout for the list tables (components/ui/VirtualTable.tsx): which columns show at each
 * screen width, and each one's fixed grid track there.
 *
 * Breakpoints are Tailwind's (sm 40rem, md 48rem, lg 64rem, xl 80rem). A table sets one CSS
 * variable per breakpoint, `--cols-base` … `--cols-xl`, holding the tracks of the columns shown at
 * that width; every line of the table (header and rows) lays out on whichever applies
 * (TABLE_GRID), and a column's cells are hidden below the width it shows from (cellClass). Plain
 * CSS, so the columns follow the screen without a re-render, and header and rows always agree.
 */

import type { ReactNode } from "react";

export const BREAKPOINTS = ["base", "sm", "md", "lg", "xl"] as const;
export type Breakpoint = (typeof BREAKPOINTS)[number];
type Above = Exclude<Breakpoint, "base">;

export interface TableColumn {
  key: string;
  header: ReactNode;
  /** The grid track: a fixed width ("6rem") or a flexible one ("minmax(0,2fr)"), or one per
   *  breakpoint, mobile first: `{ base: "4.5rem", sm: "9rem" }`. Keep tracks independent of the
   *  content (no `auto`), so the header lines up with every row. */
  width: string | ({ base: string } & Partial<Record<Above, string>>);
  /** Shown from this breakpoint up; below it the column is left out, header and cells. */
  from?: Above;
  align?: "end" | "center";
}

/** The column's track at a breakpoint, or null where the column isn't shown. */
export function trackAt(column: TableColumn, bp: Breakpoint): string | null {
  const at = BREAKPOINTS.indexOf(bp);
  if (column.from && at < BREAKPOINTS.indexOf(column.from)) return null;
  if (typeof column.width === "string") return column.width;
  let track = column.width.base;
  for (const b of BREAKPOINTS.slice(1, at + 1) as Above[]) track = column.width[b] ?? track;
  return track;
}

/** `grid-template-columns` for each breakpoint, as the CSS variables TABLE_GRID reads. */
export function columnTemplates(columns: TableColumn[]): Record<string, string> {
  return Object.fromEntries(
    BREAKPOINTS.map((bp) => [
      `--cols-${bp}`,
      columns
        .map((c) => trackAt(c, bp))
        .filter((t): t is string => t !== null)
        .join(" "),
    ]),
  );
}

// Written out in full so Tailwind's scanner generates them.
const HIDDEN_BELOW: Record<Above, string> = {
  sm: "max-sm:hidden",
  md: "max-md:hidden",
  lg: "max-lg:hidden",
  xl: "max-xl:hidden",
};
const ALIGN = { end: "text-right", center: "text-center" } as const;

/** A header or body cell of the column: hidden below its breakpoint, aligned, free to shrink. */
export function cellClass(column: TableColumn): string {
  return ["min-w-0", column.from && HIDDEN_BELOW[column.from], column.align && ALIGN[column.align]]
    .filter(Boolean)
    .join(" ");
}

/** Every line of a table, header and rows alike: the grid of the columns shown at this width. */
export const TABLE_GRID =
  "grid grid-cols-(--cols-base) sm:grid-cols-(--cols-sm) md:grid-cols-(--cols-md) lg:grid-cols-(--cols-lg) xl:grid-cols-(--cols-xl) items-center gap-x-2 sm:gap-x-3";
